import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  InputAdornment,
  alpha,
  useTheme,
  LinearProgress,
} from "@mui/material";
import {
  Save,
  Person,
  Security,
  Email,
  Phone,
  Groups,
  CalendarToday,
  Login,
  Badge,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { userAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

const Profile = () => {
  const { user, updateUser } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    team: "",
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone: user.phone || "",
        team: user.team || "",
      });
    }
  }, [user]);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(
      () => setAlert({ open: false, message: "", severity: "success" }),
      5000
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userAPI.updateUser(user.id, profileForm);
      updateUser({ ...user, ...profileForm });
      showAlert("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      showAlert("Error updating profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value,
    });
  };

  if (!user) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", py: 4 }}>
          <LinearProgress sx={{ width: "100%", mb: 2 }} />
          <Typography color="text.secondary">Loading profile...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {alert.open && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Alert
            severity={alert.severity}
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            {alert.message}
          </Alert>
        </motion.div>
      )}

      {/* Header */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            fontWeight="800"
            sx={{
              background: "linear-gradient(135deg, #005b82 0%, #2586bc 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            My Profile
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ opacity: 0.8 }}>
            Manage your personal information and account settings
          </Typography>
        </Box>
      </motion.div> */}

      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.3)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Person sx={{ fontSize: 32, color: "#005b82" }} />
                  <Typography variant="h5" fontWeight="700" color="#005b82">
                    Personal Information
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="first_name"
                        value={profileForm.first_name}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Badge sx={{ color: alpha("#005b82", 0.7) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#2586bc",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="last_name"
                        value={profileForm.last_name}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Badge sx={{ color: alpha("#005b82", 0.7) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#2586bc",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={profileForm.email}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: alpha("#005b82", 0.7) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#2586bc",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone sx={{ color: alpha("#005b82", 0.7) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#2586bc",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Team/Department"
                        name="team"
                        value={profileForm.team}
                        disabled
                        readOnly={true}
                        
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Groups sx={{ color: alpha("#005b82", 0.7) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#2586bc",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={loading ? null : <Save />}
                          disabled={loading}
                          sx={{
                            minWidth: 180,
                            height: 48,
                            borderRadius: 2,
                            background:
                              "linear-gradient(135deg, #005b82 0%, #2586bc 100%)",
                            boxShadow: "0 4px 15px rgba(0, 91, 130, 0.3)",
                            fontSize: "1rem",
                            fontWeight: 600,
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #004a6d 0%, #1c76a8 100%)",
                              boxShadow: "0 6px 20px rgba(0, 91, 130, 0.4)",
                            },
                            "&:disabled": {
                              background: theme.palette.grey[300],
                            },
                          }}
                        >
                          {loading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LinearProgress
                                sx={{ width: 20, height: 2 }}
                                color="inherit"
                              />
                              Saving...
                            </Box>
                          ) : (
                            "Save Changes"
                          )}
                        </Button>
                      </motion.div>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Profile Summary Sidebar */}
        {/* <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
          
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                background: "linear-gradient(135deg, #005b82 0%, #003d5c 100%)",
                color: "white",
                mb: 3,
                boxShadow: "0 8px 32px rgba(0, 91, 130, 0.3)",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "#2586bc",
                    fontSize: "2.5rem",
                    fontWeight: 600,
                    mx: "auto",
                    mb: 3,
                    boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                    border: "3px solid rgba(255,255,255,0.2)",
                  }}
                >
                  {user.first_name?.[0]}
                  {user.last_name?.[0]}
                </Avatar>

                <Typography variant="h5" gutterBottom fontWeight="700">
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.username}
                </Typography>

                <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                  @{user.username}
                </Typography>

                <Chip
                  label={user.role_name || "No Role"}
                  sx={{
                    background: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                    backdropFilter: "blur(10px)",
                    mb: 3,
                  }}
                />

                <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.2)" }} />

                <Box sx={{ textAlign: "left" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Member since
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(user.date_joined).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Login sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Last login
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleString()
                          : "Never"}
                      </Typography>
                    </Box>
                  </Box>

                  {user.team && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Groups sx={{ fontSize: 20, opacity: 0.8 }} />
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Team
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {user.team}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

       
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.3)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Security sx={{ fontSize: 24, color: "#005b82" }} />
                  <Typography variant="h6" fontWeight="700" color="#005b82">
                    Account Status
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Account Status
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {user.is_active ? (
                      <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />
                    ) : (
                      <Cancel sx={{ color: "#f44336", fontSize: 20 }} />
                    )}
                    <Chip
                      label={user.is_active ? "Active" : "Inactive"}
                      color={user.is_active ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    User Role
                  </Typography>
                  <Chip
                    label={user.role_name || "No Role"}
                    sx={{
                      background: alpha("#005b82", 0.1),
                      color: "#005b82",
                      fontWeight: 600,
                    }}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid> */}
      </Grid>
    </Container>
  );
};

export default Profile;
